import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { ShieldCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import EmptyStateCard from "../../EmptyStateCard";
import { saasApi } from "../shared/saasApi";
import { FindingStatus, FindingType, RiskLevel } from "../shared/saasTypes";
import { FINDING_LABELS, formatNumber, humanize, PAGE_SIZE, RISK_ORDER } from "../shared/saasFormat";
import { ListSkeleton, ListToolbar } from "../shared/SaasUi";
import FindingCard from "./FindingCard";
import { WorkspaceContext } from "./workspaceContext";

const ALL = "ALL";

export default function FindingsTab({ context }: { context: WorkspaceContext }) {
    const [status, setStatus] = useState<FindingStatus>("OPEN");
    const [severity, setSeverity] = useState<RiskLevel | undefined>();
    const [findingType, setFindingType] = useState<FindingType | undefined>();
    const [kind, setKind] = useState<"all" | "posture" | "alerts">("all");
    const [page, setPage] = useState(1);

    useEffect(() => setPage(1), [status, severity, findingType, kind, context.connectionId]);

    const summaryQuery = useQuery(["saas-findings", "summary", context.provider, context.tenantName, context.connectionId], () =>
        saasApi.findingSummary(context.provider, context.tenantName, context.connectionId));
    const findingsQuery = useQuery(
        ["saas-findings", context.provider, context.tenantName, context.connectionId, status, severity, findingType, kind, page],
        () => saasApi.findings(context.provider, context.tenantName, context.connectionId, {
            status,
            severity,
            findingType,
            posture: kind === "all" ? undefined : kind === "posture",
        }, page - 1, PAGE_SIZE),
        { keepPreviousData: true },
    );

    const summary = summaryQuery.data;
    const result = findingsQuery.data;

    return (
        <div className="space-y-2">
            <ListToolbar page={page} totalPages={result?.totalPages ?? 1} onPageChange={setPage} total={result?.totalItems ?? 0} pageSize={PAGE_SIZE}
                         label="Finding pages">
                <Select value={status} onValueChange={(value) => setStatus(value as FindingStatus)}>
                    <SelectTrigger className="saas-control w-44" aria-label="Finding status">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {(["OPEN", "RESOLVED", "DISMISSED"] as FindingStatus[]).map((value) => (
                            <SelectItem key={value} value={value}>
                                {humanize(value)}
                                {value === "OPEN" && summary ? ` (${formatNumber(summary.open)})` : ""}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={severity ?? ALL} onValueChange={(value) => setSeverity(value === ALL ? undefined : (value as RiskLevel))}>
                    <SelectTrigger className="saas-control w-44" aria-label="Severity">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>All severities</SelectItem>
                        {RISK_ORDER.filter((level) => level !== "INFO").map((level) => (
                            <SelectItem key={level} value={level}>
                                {humanize(level)}
                                {status === "OPEN" && summary ? ` (${formatNumber(summary.bySeverity[level] ?? 0)})` : ""}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={kind} onValueChange={(value) => setKind(value as typeof kind)}>
                    <SelectTrigger className="saas-control w-48" aria-label="Finding kind">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Posture and alerts</SelectItem>
                        <SelectItem value="posture">Posture and access</SelectItem>
                        <SelectItem value="alerts">Activity alerts</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={findingType ?? ALL} onValueChange={(value) => setFindingType(value === ALL ? undefined : (value as FindingType))}>
                    <SelectTrigger className="saas-control w-60" aria-label="Finding type">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>All finding types</SelectItem>
                        {(Object.keys(FINDING_LABELS) as FindingType[]).map((type) => (
                            <SelectItem key={type} value={type}>
                                {FINDING_LABELS[type]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </ListToolbar>

            {findingsQuery.isLoading ? (
                <ListSkeleton />
            ) : !result || result.items.length === 0 ? (
                <EmptyStateCard
                    icon={ShieldCheck}
                    title={status === "OPEN" ? "No open findings" : `No ${humanize(status).toLowerCase()} findings`}
                    description="Findings are evaluated after every sync and as new audit activity arrives."
                    className="cloud-card-surface border-dashed rounded-2xl"
                />
            ) : (
                result.items.map((finding) => (
                    <FindingCard
                        key={finding.id}
                        finding={finding}
                        context={context}
                        onChanged={() => {
                            findingsQuery.refetch();
                            summaryQuery.refetch();
                        }}
                    />
                ))
            )}
        </div>
    );
}
